import * as React from "react";
import { useParams } from "react-router";

import { Button, Container, Grid, Paper, Stack, TextField, Typography, IconButton, Tooltip, ToggleButton, ToggleButtonGroup } from "@mui/material";

import TextFieldsIcon from "@mui/icons-material/TextFields";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";

import CustomForm from "../../../helper-components/form/CustomForm";
import PaperFragment from "../../../helper-components/misc/PaperFragment";
import NavigationFooter from "../../../helper-components/misc/NavigationFooter";
import PaperText from "../../../helper-components/misc/PaperText.jsx";
import CustomButton from "../../../helper-components/input/CustomButton.jsx";

import { createUploadObject, isEmpty } from "../../../helper-functions/shared/miscUtils.js";
import { createQuote, deleteQuote, queryTranscript, queryTranscriptQuotes, updateQuote } from "../../../helper-functions/api/transcriptQueries.js";

import { getLanguage } from "../../../config/language/language.js";

/**
 * A page detailing a transcript.
 *
 * Includes:
 * - formatted transcript text
 * - quotes
 */
export default function TranscriptView(props) {
    const { id } = useParams(); // Get the URL parameters from react-router.

    const [transcript, setTranscript] = React.useState({});
    const [transcriptQuery, setTranscriptQuery] = React.useState(false);

    const [transcriptQuotes, setTranscriptQuotes] = React.useState([]);
    const [transcriptQuotesQuery, setTranscriptQuotesQuery] = React.useState(false);

    // Highlight functionality.
    // These are temporary states for visualizing the overlay.
    const [quoteId, setQuoteId] = React.useState(null);
    const [quoteText, setQuoteText] = React.useState("");
    const [quoteMeta, setQuoteMeta] = React.useState({
        color: "yellow",
        note: "",
        preceding_text: "",
        following_text: ""
    });

    const [overlayVisible, setOverlayVisible] = React.useState(false);
    const [overlayPosition, setOverlayPosition] = React.useState({ top: 0, left: 0 });

    const isMobile = props.browser.isMobile;

    const resources = props?.backend?.resources?.data;
    const transcripts = props?.backend?.transcripts?.data;
    const quotes = props?.backend?.quotes?.data;

    const [showOriginal, setShowOriginal] = React.useState(false);
    const hasRaw = Array.isArray(transcript?.meta_data_raw) && transcript.meta_data_raw.length > 0;
    const originalText = hasRaw ? transcript.meta_data_raw.map((caption) => caption?.text || "").join(" ") : "";
    const displayedText = showOriginal && hasRaw ? originalText : transcript?.text || "";
    const isReady = !isEmpty(transcript) && !isEmpty(displayedText);
    const resource = resources.find((r) => r.id === transcript?.resource_id);

    // Set the transcript, and potentially query it if it's not available.
    React.useEffect(() => {
        const possibleTranscript = transcripts.find((t) => t.id === parseInt(id));

        if (!isEmpty(possibleTranscript)) {
            setTranscriptQuery(true);
            setTranscript(possibleTranscript);
        }

        if (isEmpty(possibleTranscript) && !transcriptQuery) {
            setTranscriptQuery(true);
            queryTranscript(props, { id });
        }
    }, [transcripts, transcript]);

    // Query all transcript quotes.
    React.useEffect(() => {
        const possibleQuotes = quotes.filter((q) => q.transcript_id === parseInt(id));
        setTranscriptQuotes(possibleQuotes);

        if (!transcriptQuotesQuery) {
            setTranscriptQuotesQuery(true);
            queryTranscriptQuotes(props, { id });
        }
    }, [quotes]);

    /**
     * Generate highlighted text via HTML markup tags.
     * Add classes according to the color of the quote.
     * Handles overlapping quotes by moving span tags around.
     */
    const createHighlightedText = (text) => {
        if (!text) return "";

        // Step 1: Collect all quote positions
        let boundaries = transcriptQuotes.flatMap((quote) => {
            const { text: quoteText, meta_data } = quote;
            const { preceding_text, following_text, color } = meta_data;

            let positions = [];
            let matchIndex = text.indexOf(quoteText);

            while (matchIndex !== -1) {
                // Verify surrounding context to ensure correct matching
                const beforeMatch = text.slice(Math.max(0, matchIndex - preceding_text.length), matchIndex);
                const afterMatch = text.slice(matchIndex + quoteText.length, matchIndex + quoteText.length + following_text.length);

                if (beforeMatch === preceding_text && afterMatch === following_text) {
                    positions.push({ pos: matchIndex, type: "start", color, id: quote.id });
                    positions.push({ pos: matchIndex + quoteText.length, type: "end", id: quote.id });
                }

                matchIndex = text.indexOf(quoteText, matchIndex + 1);
            }

            return positions;
        });

        // Step 2: Sort boundaries (end before start if same position to avoid incorrect nesting)
        boundaries.sort((a, b) => (a.pos === b.pos ? (a.type === "end" ? -1 : 1) : a.pos - b.pos));

        // Step 3: Construct highlighted text
        let result = "";
        let currentPos = 0;
        let activeQuotes = [];

        for (const boundary of boundaries) {
            // Add non-highlighted text before this boundary
            if (boundary.pos > currentPos) {
                result += text.substring(currentPos, boundary.pos);
            }

            if (boundary.type === "start") {
                // Close active quotes before starting a new one (avoid overlap)
                while (activeQuotes.length) {
                    result += `</span>`;
                    activeQuotes.pop();
                }

                // Start new quote
                result += `<span style="cursor: pointer" class="highlight-${boundary.color}" data-quote-id='${boundary.id}'>`;
                activeQuotes.push(boundary);
            } else {
                // Close the corresponding quote
                if (activeQuotes.length && activeQuotes[activeQuotes.length - 1].id === boundary.id) {
                    result += `</span>`;
                    activeQuotes.pop();
                }
            }

            currentPos = boundary.pos;
        }

        // Append remaining text
        if (currentPos < text.length) {
            result += text.substring(currentPos);
        }

        // Ensure all open spans are closed
        while (activeQuotes.length) {
            result += `</span>`;
            activeQuotes.pop();
        }

        return result;
    };

    /**
     * Executed whenever the user selects some text on the transcript element.
     * Show an overlay with options to save the selected text as a quote.
     */
    const handleTextSelection = (e) => {
        const selection = window.getSelection();

        if (!selection || selection.rangeCount === 0) {
            resetQuoteForm();
            return;
        }

        const text = selection.toString();

        if (!text) {
            resetQuoteForm();
            return;
        }

        const fullText = transcript?.text || "";
        let selectionStart = -1;

        // Uses Range API to find the exact position of the selected text.
        if (selection.anchorNode) {
            const range = selection.getRangeAt(0);
            const preCaretRange = range.cloneRange();

            // Expand the range to the beginning of the container (entire text)
            preCaretRange.selectNodeContents(e.target);
            preCaretRange.setEnd(range.startContainer, range.startOffset);

            // The length of preCaretRange text gives us the actual selectionStart
            selectionStart = preCaretRange.toString().length;
        }

        if (selectionStart === -1) return; // Safety check

        // Capture preceding and following text (e.g., 20 characters before and after)
        const preceding_text = fullText.slice(Math.max(0, selectionStart - 20), selectionStart);
        const following_text = fullText.slice(selectionStart + text.length, selectionStart + text.length + 20);

        const { top, left } = calculateOverlayPosition(e);

        setQuoteText(text);
        setQuoteMeta({
            color: "yellow",
            note: "",
            preceding_text,
            following_text
        });

        setOverlayPosition({ top, left });
        setOverlayVisible(true);
    };

    /**
     * Handles touch selection for mobile devices
     */
    const handleTouchEnd = (e) => {
        // Use setTimeout to ensure the selection has been made before processing
        setTimeout(() => {
            const selection = window.getSelection();

            if (!selection || selection.rangeCount === 0 || selection.toString() === "") {
                // If no text is selected, this might be a tap on a highlight
                // We'll handle that in handleTouchTap
                return;
            }

            // Process the selection as in handleTextSelection
            handleTextSelection(e.changedTouches ? e.changedTouches[0] : e);
        }, 100);
    };

    /**
     * Handles tap events on mobile for clicking on highlighted quotes
     */
    const handleTouchTap = (e) => {
        // Prevent default to avoid zooming or other default behaviors
        e.preventDefault();

        const selection = window.getSelection();

        // If there's text selected, don't process as a tap
        if (selection && selection.toString() !== "") {
            return;
        }

        // Get the touch position
        const touch = e.changedTouches ? e.changedTouches[0] : e;

        // Get the element at touch position
        const element = document.elementFromPoint(touch.clientX, touch.clientY);

        // Check if this element has a quote ID
        const dataId = element?.attributes?.["data-quote-id"]?.value;
        if (!dataId) {
            resetQuoteForm();
            return;
        }

        // Position the overlay at the touch point
        const { top, left } = calculateOverlayPosition(touch);
        setOverlayPosition({ top, left });

        // Set the quote details in the overlay
        const quote = transcriptQuotes.find((q) => q.id === parseInt(dataId));
        if (!quote) return;

        setQuoteId(quote.id);
        setQuoteText(quote.text);
        setQuoteMeta(quote.meta_data);
        setOverlayVisible(true);
    };

    const resetQuoteForm = () => {
        setOverlayVisible(false);

        setQuoteId(null);
        setQuoteText("");
        setQuoteMeta({
            color: "yellow",
            note: "",
            preceding_text: "",
            following_text: ""
        });
    };

    /**
     * Make sure the overlay stays in view and doesn't go off-screen.
     */
    const calculateOverlayPosition = (e) => {
        // Get the click/touch position and set the overlay position.
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const overlayWidth = 250; // width of the overlay

        let top = (e.clientY || e.pageY) + window.scrollY + 20;
        let left = e.clientX || e.pageX;

        // For touch events on mobile, position the overlay where it's easier to interact with
        if (isMobile) {
            // On smaller screens, center the overlay horizontally
            if (windowWidth < 500) {
                left = windowWidth / 2 - overlayWidth / 2;
            }

            // On mobile, position overlay at a fixed distance from the top
            // to ensure it's visible and not under the user's finger
            top = window.scrollY + 75;
        } else {
            // Ensure left position doesn't go beyond right edge
            left = Math.min(left, windowWidth - overlayWidth - 20);
            // Ensure left isn't negative
            left = Math.max(20, left);

            // Ensure top position stays in view
            if (top + 200 > window.scrollY + windowHeight) {
                top = (e.clientY || e.pageY) + window.scrollY - 200; // move above cursor
            }
        }

        return { top, left };
    };

    /**
     * Executed whenever the user simply clicks on the transcript element (not selecting text).
     * If the click is on a highlighted quote, show the overlay with the quote's details.
     */
    const handleClick = (e) => {
        const dataId = e?.target?.attributes?.["data-quote-id"]?.value;
        if (!dataId) return;

        const { top, left } = calculateOverlayPosition(e);
        setOverlayPosition({ top, left });

        // Set the quote details in the overlay.
        const quote = transcriptQuotes.find((q) => q.id === parseInt(dataId));
        if (!quote) return;

        setQuoteId(quote.id);
        setQuoteText(quote.text);
        setQuoteMeta(quote.meta_data);

        setOverlayVisible(true);
    };

    /**
     * Executed whenever the user clicks on the save button in the overlay.
     * If the quoteId is set, update the quote, otherwise create a new one.
     */
    const handleSaveQuote = () => {
        if (quoteId) {
            const body = createUploadObject({ text: quoteText, meta_data: quoteMeta }, ["form"]);
            updateQuote(props, { id: quoteId, body });
        } else {
            const body = createUploadObject({ text: quoteText, meta_data: quoteMeta }, ["form"]);
            createQuote(props, { id: transcript.id, body });
        }

        resetQuoteForm();
    };

    /**
     * Executed whenever the user clicks on the delete button in the overlay.
     */
    const handleDeleteQuote = () => {
        deleteQuote(props, { id: quoteId });

        resetQuoteForm();
    };

    // Prepare event handlers based on device type. Quote selection stays on cleaned text.
    const transcriptEventHandlers =
        showOriginal
            ? {}
            : isMobile
              ? {
                    onTouchEnd: handleTouchEnd,
                    onContextMenu: handleTouchEnd, // Android: text selection -> context menu opened
                    onTouchStart: () => {}, // Empty handler to enable touch events
                    onClick: handleTouchTap
                }
              : {
                    onMouseUp: handleTextSelection,
                    onMouseDown: () => resetQuoteForm(),
                    onClick: handleClick
                };

    const handleCopyToClipboard = () => {
        navigator.clipboard
            .writeText(resource?.name + "\n\n" + displayedText)
            .then(() => {
                props.showSnackbar({ message: "Text copied to clipboard!", color: "info" });
            })
            .catch((error) => {
                console.error("Failed to copy: ", error);
                props.showSnackbar({ message: "Failed to copy text", color: "error" });
            });
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <CustomForm
                        title={getLanguage().TRANSCRIPT_VIEW_TITLE}
                        subtitle={getLanguage().TRANSCRIPT_VIEW_SUBTITLE}
                        icon={<TextFieldsIcon />}
                        form={
                            <Stack direction="row" mt={2} spacing={1} flexWrap="wrap" useFlexGap>
                                <CustomButton {...props} title="Edit transcript" size="small" path={`/database/transcripts/${id}/edit`} />
                                <CustomButton
                                    {...props}
                                    title="Open URL"
                                    size="small"
                                    onClick={() => window.open(resource?.url, "_blank")}
                                    disabled={!resource?.url}
                                    variant="outlined"
                                />
                                <CustomButton {...props} title="Copy" size="small" onClick={handleCopyToClipboard} disabled={!isReady} variant="outlined" />
                                {hasRaw && (
                                    <ToggleButtonGroup
                                        exclusive
                                        size="small"
                                        value={showOriginal ? "original" : "cleaned"}
                                        onChange={(event, value) => {
                                            if (!value) return;
                                            setShowOriginal(value === "original");
                                            resetQuoteForm();
                                        }}>
                                        <ToggleButton value="cleaned">Cleaned</ToggleButton>
                                        <ToggleButton value="original">Original</ToggleButton>
                                    </ToggleButtonGroup>
                                )}
                            </Stack>
                        }
                    />
                </Grid>

                {isReady && (
                    <Grid item xs={12}>
                        <PaperText bold center value={resource?.name || "Title: N/A"} />
                    </Grid>
                )}

                {transcriptQuery && !isReady && (
                    <Grid item xs={12}>
                        <PaperText value={getLanguage().NO_TRANSCRIPT_FOUND} />
                    </Grid>
                )}

                {isReady && (
                    <Grid item xs={12} {...transcriptEventHandlers}>
                        <PaperFragment
                            p={1.5}
                            value={
                                showOriginal ? (
                                    <div style={{ whiteSpace: "pre-wrap" }}>{displayedText || "N/A"}</div>
                                ) : (
                                    <div style={{ whiteSpace: "pre-wrap" }} dangerouslySetInnerHTML={{ __html: createHighlightedText(transcript?.text) ?? "N/A" }} />
                                )
                            }
                        />
                    </Grid>
                )}

                <NavigationFooter
                    rows={2}
                    options={[
                        {
                            path: `/database/transcripts`,
                            title: "Back to transcripts",
                            variant: "outlined",
                            icon: <TextFieldsIcon />
                        },
                        {
                            path: `/database/resources`,
                            title: "Resources",
                            variant: "outlined",
                            icon: <DescriptionIcon />
                        },
                        {
                            path: `/database/transcripts/${id}/edit`,
                            title: "Edit transcript",
                            variant: "contained",
                            icon: <EditIcon />
                        }
                    ]}
                />
            </Grid>

            {overlayVisible && (
                <Paper
                    style={{
                        position: "absolute",
                        top: overlayPosition.top,
                        left: overlayPosition.left,
                        padding: "10px",
                        zIndex: 1000,
                        width: "250px",
                        fontSize: "0.9em",
                        userSelect: "none"
                    }}>
                    <Stack direction="row" spacing={1} style={{ justifyContent: "space-evenly", alignItems: "center" }}>
                        {["yellow", "red", "green", "blue"].map((color) => (
                            <div
                                key={color}
                                onClick={() => setQuoteMeta((prev) => ({ ...prev, color }))}
                                style={{
                                    width: quoteMeta?.color === color ? 30 : 25,
                                    height: quoteMeta?.color === color ? 30 : 25,
                                    borderRadius: "50%",
                                    backgroundColor: color,
                                    cursor: "pointer",
                                    border: quoteMeta?.color === color ? "3px solid black" : "1px solid gray"
                                }}
                            />
                        ))}
                    </Stack>

                    <TextField
                        label="Note"
                        value={quoteMeta?.note}
                        onChange={(e) => setQuoteMeta((prev) => ({ ...prev, note: e.target.value }))}
                        fullWidth
                        multiline
                        minRows={2}
                        margin="normal"
                        size="small"
                    />

                    <Stack direction="row" m={0} p={0} style={{ justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body2" style={{ marginLeft: "3px" }}>
                            Quote ID: {quoteId ?? "N/A"}
                        </Typography>

                        <Tooltip title="Delete quote">
                            <IconButton onClick={handleDeleteQuote} size="small" disabled={!quoteId}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Copy quote">
                            <IconButton
                                onClick={() => {
                                    navigator.clipboard
                                        .writeText(quoteText)
                                        .then(() => {
                                            props.showSnackbar({ message: "Quote copied to clipboard!", color: "info" });
                                        })
                                        .catch((error) => {
                                            console.error("Failed to copy: ", error);
                                            props.showSnackbar({ message: "Failed to copy quote", color: "error" });
                                        });
                                }}
                                size="small"
                                disabled={!quoteText}>
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Button variant="contained" onClick={handleSaveQuote} size="small">
                            {quoteId ? "Update" : "Add"}
                        </Button>
                    </Stack>
                </Paper>
            )}
        </Container>
    );
}
